import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getMissions } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Package, MapPin, Calendar, Loader2, AlertCircle } from 'lucide-react';

const MissionsPage = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMissions();
  }, [filter]);

  const fetchMissions = async () => {
    try {
      setLoading(true);
      const status = filter !== 'all' ? filter : undefined;
      const data = await getMissions(status);
      setMissions(data);
    } catch (error) {
      console.error('Failed to fetch missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', className: 'status-pending' },
      matched: { label: 'Attribué', className: 'status-matched' },
      in_progress: { label: 'En cours', className: 'status-in_progress' },
      completed: { label: 'Terminé', className: 'status-completed' },
      cancelled: { label: 'Annulé', className: 'status-cancelled' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100' };
    return <Badge className={`badge ${config.className}`}>{config.label}</Badge>;
  };

  const filteredMissions = missions.filter(mission => 
    mission.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mission.pickup_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mission.delivery_address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="missions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              Mes Missions
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez et suivez vos missions de transport
            </p>
          </div>
          <Link to="/missions/new">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="create-mission-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Mission
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher une mission..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-missions-input"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-status-select">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="matched">Attribué</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Missions List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredMissions.length > 0 ? (
          <div className="grid gap-4">
            {filteredMissions.map((mission) => (
              <Link 
                key={mission.id} 
                to={`/missions/${mission.id}`}
                data-testid={`mission-card-${mission.id}`}
              >
                <Card className="hover:shadow-lg hover:border-orange-200 transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-slate-900">{mission.title}</h3>
                          {getStatusBadge(mission.status)}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-emerald-500" />
                            <span className="truncate">{mission.pickup_address}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-red-500" />
                            <span className="truncate">{mission.delivery_address}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                          <span>
                            <span className="font-['JetBrains_Mono'] text-slate-700">{mission.weight_kg}</span> kg
                          </span>
                          <span>
                            <span className="font-['JetBrains_Mono'] text-slate-700">{mission.volume_m3}</span> m³
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(mission.deadline).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      <div className="text-right md:text-right">
                        <div className="font-['JetBrains_Mono'] font-bold text-xl text-slate-900">
                          {mission.budget_min}€ - {mission.budget_max}€
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                          <span className="font-['JetBrains_Mono'] text-orange-500">{mission.bids_count}</span> offre(s)
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="empty-state">
                <Package className="empty-state-icon" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune mission trouvée</h3>
                <p className="text-slate-500 mb-4">
                  {searchQuery ? 'Essayez avec d\'autres termes de recherche' : 'Créez votre première mission'}
                </p>
                {!searchQuery && (
                  <Link to="/missions/new">
                    <Button className="bg-orange-500 hover:bg-orange-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer une mission
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default MissionsPage;
